import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase, supabaseAdmin } from '../../config/supabase';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from '../../components/ui/use-toast';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

interface Profession {
  id: string;
  name: string;
  description?: string;
  category?: string;
  is_accepted?: boolean;
}

const Categories = () => {
  const location = useLocation();
  const { adminUser } = useAdminAuth();
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Determine which table to query based on URL parameter
  const queryParams = new URLSearchParams(location.search);
  const tableParam = queryParams.get('table') || 'professions';
  
  let tableName = 'professions';
  if (tableParam === 'craft_professions') {
    tableName = 'craft_professions';
  } else if (tableParam === 'hospitality_categories') {
    tableName = 'hospitality_categories';
  } else if (tableParam === 'professionals_categories') {
    tableName = 'professionals_categories';
  }

  const categorySummaries = React.useMemo(
    () => {
      const counts = new Map<string, number>();
      professions.forEach((p) => {
        const key = (p.category || '').trim();
        if (!key) {
          return;
        }
        counts.set(key, (counts.get(key) || 0) + 1);
      });

      return Array.from(counts.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([category, count]) => ({ category, count }));
    },
    [professions]
  );

  useEffect(() => {
    fetchProfessions();
  }, [tableParam]);

  const fetchProfessions = async () => {
    if (!adminUser) {
      toast({
        title: 'Unauthorized',
        description: 'You do not have permission to view categories.',
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching category source rows from table:', tableName);
      
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .order('name');

      console.log('Fetch response:', { data, error });

      if (error) {
        console.error('Supabase fetch error:', error);
        throw error;
      }

      setProfessions(data || []);
    } catch (error) {
      console.error(`Error fetching ${tableName}:`, error);
      toast({
        title: 'Error',
        description: `Failed to load ${tableName}: ${(error as Error).message}`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;

    const alreadyExists = categorySummaries.some(
      (item) => item.category.toLowerCase() === trimmed.toLowerCase()
    );
    if (alreadyExists) {
      toast({
        title: 'Category exists',
        description: 'This category already exists for this table.',
      });
      return;
    }

    try {
      setIsAdding(true);
      const insertData: any = {
        name: trimmed,
        description: null,
        category: trimmed,
      };
      
      const { data, error } = await supabaseAdmin
        .from(tableName)
        .insert(insertData)
        .select();

      if (error) throw error;

      // Update local state
      setProfessions(prev => [...prev, data[0]]);
      setNewCategoryName('');

      toast({
        title: 'Success',
        description: 'Category added successfully',
      });
    } catch (error) {
      console.error(`Error adding category to ${tableName}:`, error);
      toast({
        title: 'Error',
        description: 'Failed to add category',
        variant: 'destructive',
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteProfession = async (professionId: string | number) => {
    // Check admin authorization
    if (!adminUser) {
      toast({
        title: 'Error',
        description: 'Not authorized to perform this action',
        variant: 'destructive',
      });
      return;
    }

    // Convert professionId to string
    const professionIdString = String(professionId);

    // Find the profession to be deleted
    const profession = professions.find(p => String(p.id) === professionIdString);

    if (!window.confirm(`Are you sure you want to delete the profession "${profession?.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      console.log('Starting deletion process for profession:', professionIdString);
      
      // First, check if the profession still exists
      const { data: existingProfession, error: checkError } = await supabaseAdmin
        .from(tableName)
        .select('id, name')
        .eq('id', professionIdString)
        .single();

      if (checkError) {
        console.error('Error checking profession existence:', checkError);
        throw new Error('Failed to verify profession record');
      }

      if (!existingProfession) {
        console.log('Profession already deleted:', professionIdString);
        setProfessions(prev => prev.filter(p => String(p.id) !== professionIdString));
        toast({
          title: 'Info',
          description: 'Profession record no longer exists',
        });
        return;
      }

      console.log('Found profession:', existingProfession);

      // Delete the profession record
      console.log('Deleting profession record:', professionIdString);
      const { error: deleteProfessionError } = await supabaseAdmin
        .from(tableName)
        .delete()
        .eq('id', professionIdString);

      if (deleteProfessionError) {
        console.error('Error deleting profession:', deleteProfessionError);
        throw new Error('Failed to delete profession record');
      }

      console.log('Profession deleted successfully');

      // Refresh the professions list
      await fetchProfessions();

      toast({
        title: 'Success',
        description: `Profession "${existingProfession.name}" deleted successfully`,
      });
    } catch (error) {
      console.error(`Deletion error for profession ${professionIdString}:`, error);
      
      toast({
        title: 'Deletion Error',
        description: (error as Error).message || 'Failed to delete profession',
        variant: 'destructive',
      });

      // Refresh the list to ensure UI is in sync
      await fetchProfessions();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin w-12 h-12 text-gray-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">
          {tableName === 'professions'
            ? 'Service Categories'
            : tableName === 'craft_professions'
            ? 'Craft Categories'
            : tableName === 'hospitality_categories'
            ? 'Hospitality Categories'
            : 'Professional Categories'}
        </h1>
        
        {/* Navigation tabs */}
        <div className="flex space-x-4">
          <a
            href="/admin/categories?table=professions"
            className={`px-4 py-2 rounded-md ${
              tableName === 'professions'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Service Categories
          </a>
          <a
            href="/admin/categories?table=craft_professions"
            className={`px-4 py-2 rounded-md ${
              tableName === 'craft_professions'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Craft Categories
          </a>
          <a
            href="/admin/categories?table=hospitality_categories"
            className={`px-4 py-2 rounded-md ${
              tableName === 'hospitality_categories'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Hospitality Categories
          </a>
          <a
            href="/admin/categories?table=professionals_categories"
            className={`px-4 py-2 rounded-md ${
              tableName === 'professionals_categories'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Professional Categories
          </a>
        </div>
      </div>

      {/* Category List */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">
          Existing Categories
        </h2>
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left p-2">Category</th>
              <th className="text-left p-2">Professions in Category</th>
            </tr>
          </thead>
          <tbody>
            {categorySummaries.map((item) => (
              <tr key={item.category} className="border-b hover:bg-gray-50">
                <td className="p-2 text-gray-800">{item.category}</td>
                <td className="p-2 text-gray-600">{item.count}</td>
              </tr>
            ))}
            {categorySummaries.length === 0 && (
              <tr>
                <td colSpan={2} className="p-4 text-center text-gray-500">
                  No categories found for this table.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Category Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">
          Add New Category
        </h2>
        <form onSubmit={handleAddCategory} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category name
            </label>
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <button
            type="submit"
            disabled={isAdding}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            {isAdding ? (
              <Loader2 className="animate-spin w-5 h-5 mr-2" />
            ) : (
              <Plus className="w-5 h-5 mr-2" />
            )}
            Add Category
          </button>
        </form>
      </div>
    </div>
  );
};

export default Categories;
